import { CookieSettingsButton } from "@/shared/components/consent/CookieSettingsButton";
import {
  PolicyList,
  PolicyPage,
  PolicyParagraph,
  PolicySection,
  PolicySubsection,
} from "./policy/PolicyPage";

/**
 * Cookie policy. Built from the shared `PolicyPage` kit so it matches the
 * forthcoming privacy and terms pages. The "Managing Cookies" section embeds the
 * `CookieSettingsButton` (the real preference center) so visitors can revisit
 * their choice after dismissing the banner (design.md §13.1).
 */
export function CookiePolicy() {
  return (
    <PolicyPage title="Cookie Policy" lastUpdated="June 12, 2026">
      <PolicySection title="What Are Cookies?">
        <PolicyParagraph>
          Cookies are small text files placed on your device when you visit our
          website or use our Service.
        </PolicyParagraph>
        <PolicyParagraph>
          They help us operate the Service, remember preferences, improve
          functionality, and understand usage patterns.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Types of Cookies We Use">
        <PolicySubsection title="Essential Cookies">
          <PolicyParagraph>
            These cookies are necessary for the operation of the Service.
          </PolicyParagraph>
          <PolicyParagraph>Examples include:</PolicyParagraph>
          <PolicyList
            items={[
              "User authentication",
              "Session management",
              "Security controls",
              "Account login functionality",
            ]}
          />
          <PolicyParagraph>
            Without these cookies, portions of the Service may not function
            properly.
          </PolicyParagraph>
        </PolicySubsection>

        <PolicySubsection title="Functional Cookies">
          <PolicyParagraph>
            These cookies remember settings and preferences such as:
          </PolicyParagraph>
          <PolicyList
            items={[
              "Language preferences",
              "User interface settings",
              "Login preferences",
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="Analytics Cookies">
          <PolicyParagraph>
            These cookies help us understand how users interact with the
            Service.
          </PolicyParagraph>
          <PolicyParagraph>
            Examples include information regarding:
          </PolicyParagraph>
          <PolicyList
            items={[
              "Page views",
              "Feature usage",
              "Performance metrics",
              "Error reporting",
            ]}
          />
          <PolicyParagraph>
            Analytics data is generally aggregated and used to improve the
            Service.
          </PolicyParagraph>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="Third-Party Cookies">
        <PolicyParagraph>We may use third-party providers for:</PolicyParagraph>
        <PolicyList
          items={["Analytics", "Authentication", "Infrastructure monitoring"]}
        />
        <PolicyParagraph>
          These providers may place cookies or similar technologies on your
          device.
        </PolicyParagraph>
        <PolicyParagraph>
          Their use of information is governed by their own privacy policies.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Managing Cookies">
        <PolicyParagraph>You can control cookies through:</PolicyParagraph>
        <PolicyList
          items={[
            "Browser settings",
            "Device settings",
            "Cookie preference tools provided on our website",
          ]}
        />
        <PolicyParagraph>
          Please note that disabling certain cookies may affect Service
          functionality.
        </PolicyParagraph>
        <div>
          <CookieSettingsButton variant="outline" size="lg" />
        </div>
      </PolicySection>

      <PolicySection title="Cookie Consent">
        <PolicyParagraph>
          Where required by applicable law, we obtain consent before placing
          non-essential cookies on your device.
        </PolicyParagraph>
        <PolicyParagraph>
          You may withdraw consent at any time through our cookie preference
          center.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Updates">
        <PolicyParagraph>
          We may update this Cookie Policy periodically.
        </PolicyParagraph>
        <PolicyParagraph>
          Changes will be reflected by the updated &ldquo;Last Updated&rdquo;
          date.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Contact">
        <PolicyParagraph>
          Questions about this Cookie Policy may be directed to:
        </PolicyParagraph>
        <p className="type-body-base text-text">Artisaire</p>
        <p className="type-body-base text-text-muted">
          Email:{" "}
          <a
            href="mailto:info@artisaire.com"
            className="text-brand underline-offset-4 hover:underline"
          >
            info@artisaire.com
          </a>
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
